import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  ShoppingCartIcon,
  UserIcon,
  Bars3Icon,
  MicrophoneIcon,
  HomeIcon,
  ClipboardDocumentListIcon
} from '@heroicons/react/24/outline';
import { RootState, AppDispatch } from '../../store/store';
import ThemeDropdown from '../theme/ThemeDropdown';
import { toggleCart } from '../../store/slices/cartSlice';
import { logout } from '../../store/slices/authSlice';
import { fetchCompanySettings } from '../../store/slices/companySlice';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const { isAuthenticated, user, guestUser } = useSelector((state: RootState) => state.auth);
  const { items } = useSelector((state: RootState) => state.cart);
  const { isListening, currentSession } = useSelector((state: RootState) => state.voice);
  const { settings: companySettings } = useSelector((state: RootState) => state.company);

  // Fetch company settings on mount
  useEffect(() => {
    dispatch(fetchCompanySettings());
  }, [dispatch]);

  const cartItemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/');
  };

  const currentUser = user || guestUser;

  return (
    <header className="bg-white shadow-lg border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-sm">VR</span>
            </div>
            <span className="text-xl font-bold text-gray-900">
              {companySettings?.company_caption || 'Voice Restaurant'}
            </span>
          </Link>

          {/* Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className="flex items-center space-x-1 text-gray-700 hover:text-primary-600 transition-colors"
            >
              <HomeIcon className="w-5 h-5" />
              <span>Home</span>
            </Link>

            <Link
              to="/menu"
              className="flex items-center space-x-1 text-gray-700 hover:text-primary-600 transition-colors"
            >
              <ClipboardDocumentListIcon className="w-5 h-5" />
              <span>Menu</span>
            </Link>

            <Link
              to="/voice-order"
              className={`flex items-center space-x-1 transition-colors ${
                isListening || currentSession
                  ? 'text-green-600 animate-pulse'
                  : 'text-gray-700 hover:text-primary-600'
              }`}
            >
              <MicrophoneIcon className="w-5 h-5" />
              <span>Voice Order</span>
            </Link>

            <ThemeDropdown className="hidden sm:block" />

            {isAuthenticated && (
              <Link
                to="/order-tracking"
                className="text-gray-700 hover:text-primary-600 transition-colors"
              >
                Track Orders
              </Link>
            )}
          </nav>

          {/* User Actions */}
          <div className="flex items-center space-x-4">
            {/* Theme Dropdown - Mobile */}
            <ThemeDropdown className="sm:hidden" />

            {/* Cart */}
            <button
              onClick={() => dispatch(toggleCart())}
              className="relative p-2 text-gray-700 hover:text-primary-600 transition-colors"
            >
              <ShoppingCartIcon className="w-6 h-6" />
              {cartItemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {cartItemCount}
                </span>
              )}
            </button>

            {/* User Menu */}
            {isAuthenticated ? (
              <div className="flex items-center space-x-3">
                <span className="text-sm text-gray-700">
                  Welcome, {user?.firstName || guestUser?.name || 'Guest'}!
                </span>

                <div className="flex items-center space-x-2">
                  <Link
                    to="/profile"
                    className="p-2 text-gray-700 hover:text-primary-600 transition-colors"
                  >
                    <UserIcon className="w-6 h-6" />
                  </Link>

                  <button
                    onClick={handleLogout}
                    className="text-sm bg-gray-100 hover:bg-gray-200 px-3 py-2 rounded-md transition-colors"
                  >
                    Logout
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <Link
                  to="/login"
                  className="text-sm text-gray-700 hover:text-primary-600 transition-colors"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="text-sm bg-primary-500 text-white px-4 py-2 rounded-md hover:bg-primary-600 transition-colors"
                >
                  Sign Up
                </Link>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button className="md:hidden p-2 text-gray-700 hover:text-primary-600">
              <Bars3Icon className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>

      {/* Voice Session Status Bar */}
      {currentSession && (
        <div className="bg-green-100 border-t border-green-200 px-4 py-2">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm text-green-800">
                Voice session active - Table {currentSession.tableNumber}
              </span>
            </div>

            <Link
              to="/voice-order"
              className="text-sm text-green-700 hover:text-green-900 underline"
            >
              Continue ordering
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;